import { useAgents } from '#~/agent/hooks.js';
import { useTemplate } from '#~/device/registry/hooks.js';
import { getTransitoryDevice } from '#~/device/transitory.js';
import { Sel, STag } from '#~/stag/index.js';
import type { MenuProps } from './types.js';
import * as ContextMenu from '@studio/design-system/context-menu';
import { getMenuItemHandler } from '@studio/design-system/menu';
import { isCalibrated, isCalibrating, Status } from '@studio/models/control/calibration';
import { shortCircuit } from '@studio/react-utils/event';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { shallow } from 'zustand/shallow';

/** `<Menu>` component. */
export const Menu = ({ handler, status, device, ...props }: MenuProps): ReactElement => {
  const handleSelect = useLatestFn((event: Event) => {
    getMenuItemHandler(handler, event)?.();
  });

  const calibration = status.control?.calibration?.status;

  const agentId = status.device?.agentId;
  const agent = useAgents((a) => a.find((a_) => a_.id === agentId), shallow);

  const template = useTemplate(device.modelId);
  // ENG-4495 C6/D3: the authoritative driver-identity bit replaces the fragile
  // name.includes('other') heuristic. Tri-state, permissive on absence — an unresolved template
  // (undefined) behaves like today (the item stays visible), never hidden on `=== true` failing.
  const isGeneric = template?.model?.generic === true;

  const isTransitory = !!getTransitoryDevice(device.id);

  return (
    <ContextMenu.Content onCloseAutoFocus={shortCircuit} {...props}>
      {/* ENG-4406: a transitory credential placeholder gets the SAME
          "Advanced settings" item as every other device — the `!isTransitory`
          gate is dropped HERE only. It deep-links to the Authentication
          surface (the placeholder's one config door). It STAYS on the
          Calibrate gate below (calibration is meaningless pre-auth). The
          serialized data-action="settings" is the HIL contract — unchanged.
          Per `reference-rules-in-comments` (ai.knowledge/global). */}
      {!device?.pending && (
        <ContextMenu.Item
          {...STag(Sel.MainRoomLeftTrayVideo, 'source-settings', device.id)}
          action='settings'
          icon='settings'
          label='Advanced settings'
          onSelect={handleSelect}
        />
      )}

      <ContextMenu.Item action='rename' icon='edit' label='Rename source' onSelect={handleSelect} />

      {/* isGeneric (ED ENG-4495) replaces the old name.includes('other')
          heuristic; !isTransitory (Multi-Add) keeps Calibrate hidden for
          credential-placeholder devices (calibration is meaningless pre-auth). */}
      {!agent?.embedded && !isGeneric && !isTransitory && (
        <ContextMenu.Item
          action='calibrate'
          disabled={
            !calibration ||
            [Status.CALIBRATION_STATE_NULL, Status.CALIBRATION_STATE_RUNNING].includes(calibration)
          }
          icon='source'
          label={
            isCalibrated(calibration)
              ? 'Recalibrate'
              : isCalibrating(calibration)
                ? 'Calibrating...'
                : 'Calibrate'
          }
          onSelect={handleSelect}
        />
      )}

      {/* A credential placeholder is local to this Studio; its discovering
          Bridge must stay linked when the user removes the placeholder. */}
      {!!agent?.embedded && !isTransitory && (
        <ContextMenu.Item
          action='unlink'
          {...STag(Sel.MainRoomLeftTrayVideo, 'unlink-source', device.id)}
          icon='trash'
          label='Unlink device'
          onSelect={handleSelect}
        />
      )}

      {(!agent?.embedded || isTransitory) && (
        <ContextMenu.Item
          action='remove'
          {...STag(Sel.MainRoomLeftTrayVideo, 'delete-source', device.id)}
          icon='trash'
          label='Delete source'
          onSelect={handleSelect}
        />
      )}
    </ContextMenu.Content>
  );
};
